// The grant half of the Grants page: pick subjects (a user, a group, or a
// client), pick one role, and preview-then-commit the cartesian product of
// subjects x selected target objects through `POST /api/roles/bulk`.
import { faTrash, faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import {
  Badge,
  Button,
  Grid,
  Icon,
  Input,
  Message,
  Table,
  Tabs,
} from "@intility/bifrost-react";
import Select from "@intility/bifrost-react-select";
import { useMemo, useState } from "react";
import { useClients, useSearchUsers } from "~/api/clients";
import {
  type AccessStoreRole,
  type BulkRolesResponse,
  bulkGrantRoles,
  useGroups,
} from "~/api/grants";
import type { CatalogCompany } from "~/api/types";
import { BulkOutcomeModal } from "./BulkOutcomeModal";
import { objectLabel, ROLE_LABEL, subjectId } from "./labels";

export interface GrantSubject {
  /** The subject string sent to fga-api, `group:<name>#member` for groups. */
  subject: string;
  type: "user" | "group" | "client";
  label: string;
}

const ROLE_OPTIONS = (["admin", "writer", "drafter", "reader"] as const).map(
  (role) => ({ value: role, label: ROLE_LABEL[role] }),
);

const SUBJECT_TYPE_BADGE: Record<
  GrantSubject["type"],
  "attn" | "success" | "chill"
> = {
  user: "attn",
  client: "success",
  group: "chill",
};

export function GrantBuilder({
  targetObjects,
  companies,
  onApplied,
}: {
  targetObjects: string[];
  companies: CatalogCompany[] | undefined;
  onApplied: () => void;
}) {
  const [subjects, setSubjects] = useState<GrantSubject[]>([]);
  const [role, setRole] = useState<AccessStoreRole>("reader");
  const [tab, setTab] = useState<"users" | "groups" | "clients">("users");
  const [userQuery, setUserQuery] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [preview, setPreview] = useState<BulkRolesResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkRolesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: userHits } = useSearchUsers(
    tab === "users" && userQuery.trim().length >= 2 ? userQuery.trim() : "",
  );
  const { data: groups, isLoading: groupsLoading } = useGroups();
  const { data: clients, isLoading: clientsLoading } = useClients();

  const groupHits = useMemo(() => {
    const query = groupSearch.trim().toLowerCase();
    return (groups ?? []).filter((g) =>
      subjectId(g).toLowerCase().includes(query),
    );
  }, [groups, groupSearch]);

  const clientHits = useMemo(() => {
    const query = clientSearch.trim().toLowerCase();
    return (clients ?? []).filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.clientId.toLowerCase().includes(query),
    );
  }, [clients, clientSearch]);

  function addSubject(candidate: GrantSubject) {
    setSubjects((prev) =>
      prev.some((s) => s.subject === candidate.subject)
        ? prev
        : [...prev, candidate],
    );
  }

  function removeSubject(subject: string) {
    setSubjects((prev) => prev.filter((s) => s.subject !== subject));
  }

  const items = useMemo(
    () =>
      subjects.flatMap((s) =>
        targetObjects.map((object) => ({
          user: s.subject,
          relation: role,
          object,
        })),
      ),
    [subjects, targetObjects, role],
  );

  async function handlePreview() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setPreview(await bulkGrantRoles(items, true));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Preview failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      const applied = await bulkGrantRoles(items, false);
      setPreview(null);
      setResult(applied);
      setSubjects([]);
      onApplied();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Grant failed.");
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = subjects.length > 0 && targetObjects.length > 0 && !busy;

  return (
    <Grid gap={16}>
      <Tabs contentBackground="base-2">
        <Tabs.Item
          active={tab === "users"}
          onClick={() => setTab("users")}
          content={
            <Grid gap={8}>
              <Input
                label="Find user"
                placeholder="Type at least two characters…"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                style={{ maxWidth: 360 }}
              />
              {userQuery.trim().length >= 2 ? (
                (userHits ?? []).length === 0 ? (
                  <p className="bf-small bfc-base-2" style={{ margin: 0 }}>
                    No users match "{userQuery.trim()}".
                  </p>
                ) : (
                  <Table noBorder>
                    <Table.Body>
                      {(userHits ?? []).map((u) => (
                        <Table.Row key={u.userId}>
                          <Table.Cell>{u.displayName}</Table.Cell>
                          <Table.Cell>
                            <span className="bf-small bfc-base-2">
                              {u.email ?? u.userId}
                            </span>
                          </Table.Cell>
                          <Table.Cell style={{ width: 80 }}>
                            <Button
                              small
                              variant="flat"
                              onClick={() =>
                                addSubject({
                                  subject: `user:${u.userId}`,
                                  type: "user",
                                  label: u.displayName,
                                })
                              }
                            >
                              <Icon icon={faUserPlus} marginRight />
                              Add
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                )
              ) : (
                <p className="bf-small bfc-base-2" style={{ margin: 0 }}>
                  Search by name or email to add users.
                </p>
              )}
            </Grid>
          }
        >
          Users
        </Tabs.Item>
        <Tabs.Item
          active={tab === "groups"}
          onClick={() => setTab("groups")}
          content={
            <Grid gap={8}>
              <Input
                label="Find group"
                placeholder="Group name…"
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                style={{ maxWidth: 360 }}
              />
              {groupsLoading ? (
                <Icon.Spinner size={20} />
              ) : groupHits.length === 0 ? (
                <p className="bf-small bfc-base-2" style={{ margin: 0 }}>
                  No groups match. Groups are created through the Groups API,
                  not on this page.
                </p>
              ) : (
                <Table noBorder>
                  <Table.Body>
                    {groupHits.map((g) => {
                      const name = subjectId(g);
                      return (
                        <Table.Row key={g}>
                          <Table.Cell>{name}</Table.Cell>
                          <Table.Cell style={{ width: 80 }}>
                            <Button
                              small
                              variant="flat"
                              onClick={() =>
                                addSubject({
                                  subject: `${g}#member`,
                                  type: "group",
                                  label: name,
                                })
                              }
                            >
                              <Icon icon={faUserPlus} marginRight />
                              Add
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              )}
            </Grid>
          }
        >
          Groups
        </Tabs.Item>
        <Tabs.Item
          active={tab === "clients"}
          onClick={() => setTab("clients")}
          content={
            <Grid gap={8}>
              <Input
                label="Find client"
                placeholder="Client name or id…"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                style={{ maxWidth: 360 }}
              />
              {clientsLoading ? (
                <Icon.Spinner size={20} />
              ) : clientHits.length === 0 ? (
                <p className="bf-small bfc-base-2" style={{ margin: 0 }}>
                  No clients match.
                </p>
              ) : (
                <Table noBorder>
                  <Table.Body>
                    {clientHits.map((c) => (
                      <Table.Row key={c.id}>
                        <Table.Cell>{c.name}</Table.Cell>
                        <Table.Cell>
                          <span className="bf-small bfc-base-2">
                            {c.clientId}
                          </span>
                        </Table.Cell>
                        <Table.Cell style={{ width: 80 }}>
                          <Button
                            small
                            variant="flat"
                            onClick={() =>
                              addSubject({
                                subject: `client:${c.clientId}`,
                                type: "client",
                                label: c.name,
                              })
                            }
                          >
                            <Icon icon={faUserPlus} marginRight />
                            Add
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </Grid>
          }
        >
          Clients
        </Tabs.Item>
      </Tabs>

      {subjects.length > 0 && (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Subject</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Grant as</Table.HeaderCell>
              <Table.HeaderCell aria-label="Actions" style={{ width: 48 }} />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {subjects.map((s) => (
              <Table.Row key={s.subject}>
                <Table.Cell>{s.label}</Table.Cell>
                <Table.Cell>
                  <Badge state={SUBJECT_TYPE_BADGE[s.type]}>
                    {s.type === "user"
                      ? "User"
                      : s.type === "group"
                        ? "Group"
                        : "Client"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <code>{s.subject}</code>
                </Table.Cell>
                <Table.Cell>
                  <Button
                    small
                    variant="flat"
                    aria-label={`Remove ${s.label}`}
                    onClick={() => removeSubject(s.subject)}
                  >
                    <Icon icon={faTrash} />
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <div
        style={{
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <Select
          label="Role"
          options={ROLE_OPTIONS}
          value={ROLE_OPTIONS.find((o) => o.value === role)}
          onChange={(opt) => opt && setRole(opt.value as AccessStoreRole)}
          style={{ maxWidth: 220 }}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <Button
            variant="filled"
            state={canSubmit ? undefined : "inactive"}
            onClick={canSubmit ? handlePreview : undefined}
          >
            <Icon icon={faUserPlus} marginRight />
            {subjects.length === 0
              ? "Add subjects to grant"
              : targetObjects.length === 0
                ? "Select target objects above"
                : `Preview grant (${subjects.length * targetObjects.length})`}
          </Button>
        </div>
        <p className="bf-small bfc-base-2" style={{ margin: 0 }}>
          Applies to the {targetObjects.length} selected{" "}
          {targetObjects.length === 1 ? "object" : "objects"} above
          {subjects.length > 0 && targetObjects.length > 0 && (
            <>
              :{" "}
              {targetObjects
                .map((o) => objectLabel(o, companies))
                .slice(0, 3)
                .join(", ")}
              {targetObjects.length > 3 &&
                `, +${targetObjects.length - 3} more`}
            </>
          )}
          .
        </p>
      </div>

      {error && (
        <Message
          state="alert"
          header="Grant failed"
          onClose={() => setError(null)}
        >
          {error}
        </Message>
      )}

      {result && (
        <Message
          state={result.counts.rejected > 0 ? "warning" : "success"}
          header="Grant applied"
          onClose={() => setResult(null)}
        >
          {result.counts.granted} granted
          {result.counts.unchanged > 0 &&
            `, ${result.counts.unchanged} unchanged`}
          {result.counts.superseded > 0 &&
            `, ${result.counts.superseded} superseded`}
          {result.counts.rejected > 0 && `, ${result.counts.rejected} rejected`}
          .
          {result.items
            .filter((i) => i.outcome === "rejected")
            .map((i) => (
              <span key={i.index} style={{ display: "block" }}>
                {subjectId(i.user)} on {objectLabel(i.object, companies)}:{" "}
                {i.message ?? i.code ?? "rejected"}
              </span>
            ))}
          {result.items
            .filter((i) => (i.superseded ?? []).length > 0)
            .map((i) => (
              <span key={`superseded-${i.index}`} style={{ display: "block" }}>
                {subjectId(i.user)} on {objectLabel(i.object, companies)}: also
                removed{" "}
                {(i.superseded ?? [])
                  .map((s) => `${ROLE_LABEL[s.role] ?? s.role} on ${s.object}`)
                  .join(", ")}
                .
              </span>
            ))}
        </Message>
      )}

      {preview && (
        <BulkOutcomeModal
          mode="grant"
          result={preview}
          busy={busy}
          companies={companies}
          onConfirm={handleConfirm}
          onClose={() => setPreview(null)}
        />
      )}
    </Grid>
  );
}
